import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import withStyles from '@mui/styles/withStyles';
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

import storage from "../../core/storage/actions";
import qstorage from "../../core/storage/queries";

function styles(theme) {
  const w = theme.spacing(30);
  const createT = theme.transitions.create;
  const sharpT = theme.transitions.easing.sharp;
  const enterT = theme.transitions.duration.enteringScreen;
  const leaveT = theme.transitions.duration.leavingScreen;

  return {
    // Cor adaptada ao navbar BRANCO (antes era branca, sumia no fundo).
    input: {
      color: "#1a3a47",
      fontSize: "0.9rem",
      fontWeight: 500,
      overflow: "hidden",
      "& input::placeholder": {
        color: "rgba(96, 125, 139, 0.65)",
        opacity: 1,
      },
    },
    underline: {
      borderBottom: "1px solid rgba(0, 128, 173, 0.20)",
      "&:after": {
        borderBottom: "2px solid #0080ad",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottom: "1px solid rgba(0, 128, 173, 0.40)",
      },
    },
    show: {
      width: w,
      whiteSpace: "nowrap",
      transition: createT("width", {
        duration: enterT,
        easing: sharpT,
      }),
    },
    hide: {
      width: 0,
      overflowX: "hidden",
      transition: createT("width", {
        duration: leaveT,
        easing: sharpT,
      }),
    },
  };
}

function SearchField(props) {
  const { classes, searchTerm, setSearchTerm, searchFieldOpen, setSearchFieldOpen } = props;
  const inputRef = React.useRef(null);

  // Foca o input só quando o campo abre. Antes era `autoFocus` no Input,
  // que disparava no mount mesmo escondido (width: 0) — em mobile, abria
  // o teclado sem o usuário pedir.
  React.useEffect(() => {
    if (searchFieldOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [searchFieldOpen]);

  return (
    <>
      {!searchFieldOpen && (
        <IconButton
          onClick={() => setSearchFieldOpen(true)}
          size="large"
          aria-label="Buscar"
          sx={{
            color: '#0080ad',
            bgcolor: 'rgba(0, 128, 173, 0.08)',
            borderRadius: '10px',
            p: 1,
            ml: 0.5,
            '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.14)' },
          }}
        >
          <SearchIcon />
        </IconButton>
      )}
      <Input
        value={searchTerm}
        inputRef={inputRef}
        classes={{ underline: classes.underline }}
        onChange={(e) => setSearchTerm(e.target.value)}
        // Margem à esquerda quando o input está aberto — cria respiro
        // visual entre o botão de filtro e o campo de busca. Mesmo
        // padrão do DynamicSearchField (usado em formaturas).
        sx={{ ml: searchFieldOpen ? { xs: 1, md: 1.5 } : 0 }}
        className={classNames(classes.input, classes.show, !searchFieldOpen && classes.hide)}
        onBlur={() => {
          if (!searchTerm) {
            setSearchFieldOpen(false);
          }
        }}
        endAdornment={
          <InputAdornment position="end">
            <IconButton
              aria-label="Fechar busca rápida"
              onClick={() => {
                setSearchFieldOpen(false);
                setSearchTerm("");
              }}
              size="small"
              sx={{
                color: '#0080ad',
                p: 0.5,
                '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.10)' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />
    </>
  );
}

function mapStateToProps(state, ownProps) {
  const { view, table = "table" } = ownProps;
  return {
    searchTerm: qstorage.get(state, view, [table, "search"], ""),
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  const { view, table = "table" } = ownProps;
  const actions = {
    setSearchTerm: (term) => storage.set(view, [table, "search"], term),
  };

  return bindActionCreators(actions, dispatch);
}

SearchField.defaultProps = {
  table: "table",
};

SearchField.propTypes = {
  /**
   * view em que os dados serão salvos.
   * Deve ser a mesma view passada para o `FilterForm`.
   */
  // eslint-disable-next-line react/no-unused-prop-types
  view: PropTypes.string.isRequired,
  /**
   * Id da `Table` em que os filtros serão usados.
   */
  // eslint-disable-next-line react/no-unused-prop-types
  table: PropTypes.string,
  /**
   * Indica se o campo de busta está em modo de digitação.
   */
  searchFieldOpen: PropTypes.bool.isRequired,
  /**
   * Função de alteração da prop `searchFieldOpen`.
   */
  setSearchFieldOpen: PropTypes.func.isRequired,

  // * Props de estado
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SearchField));
